<template>
  <v-container class="check-in">
    <label class="text-h5">{{ $t('admin_check_in.title') }}</label>
    <v-input hide-details>
      <v-container fluid class="flex-column">
        <legend class="v-label theme--light">{{ $t('admin_check_in.event') }}</legend>
        <v-autocomplete outlined
                        :placeholder="$t('admin_check_in.event')"
                        :items="eventOptions"
                        @input="onEventSelected"
                        @click:clear="onEventCleared"
                        clearable></v-autocomplete>
      </v-container>
    </v-input>
    <v-input hide-details>
      <v-container fluid class="flex-column">
        <legend class="v-label theme--light">{{ $t('admin_check_in.session') }}</legend>
        <v-autocomplete outlined
                        :placeholder="$t('admin_check_in.session')"
                        :items="sessionOptions"
                        v-model="selectedSessionId"
                        clearable></v-autocomplete>
      </v-container>
    </v-input>

    <v-btn block
           color="primary"
           @click="search">
      {{ $t('admin_check_in.scan_qr_code') }}
    </v-btn>

    <v-dialog v-model="showDialog">
      <v-card>
        <v-card-title class="headline">
          {{ $t('admin_check_in.title') }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6" v-if="account">
              <v-input hide-details>
                <v-container fluid class="flex-column">
                  <legend class="v-label theme--light">{{ $t('admin_user.name_en') }}</legend>
                  <v-row>
                    <v-col>
                      <v-text-field readonly
                                    :value="account.surname_en"
                                    hide-details></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field readonly
                                    :value="account.given_name_en"
                                    hide-details></v-text-field>
                    </v-col>
                  </v-row>
                </v-container>
              </v-input>
              <v-input hide-details>
                <v-container fluid class="flex-column">
                  <legend class="v-label theme--light">{{ $t('admin_user.name_zh') }}</legend>
                  <v-row>
                    <v-col>
                      <v-text-field readonly
                                    :value="account.surname_zh"
                                    hide-details></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field readonly
                                    :value="account.given_name_zh"
                                    hide-details></v-text-field>
                    </v-col>
                  </v-row>
                </v-container>
              </v-input>
              <v-input hide-details>
                <v-container fluid class="flex-column">
                  <legend class="v-label theme--light">{{ $t('admin_user.company') }}</legend>
                  <v-row>
                    <v-col>
                      <v-text-field readonly
                                    :value="account.company.name"
                                    hide-details></v-text-field>
                    </v-col>
                  </v-row>
                </v-container>
              </v-input>
            </v-col>
            <v-col cols="12" md="6">
              <v-row v-if="account && !enrollment" class="check-in-no-record">
                <v-col>{{ $t('admin_check_in.no_record') }}</v-col>
              </v-row>
              <v-row v-else>
                <v-col>
                  <v-text-field :placeholder="$t('search')"
                                prepend-icon="fas fa-search"
                                v-model="searchInput"></v-text-field>
                  <b-table ref="selectableTable"
                           :items="filteredParticipants"
                           :fields="participantFields"
                           selectable
                           select-mode="single"
                           selectedVariant="success"
                           @row-selected="onParticipantSelected"
                           responsive
                           show-empty
                           hover
                           empty-text="No Participant"
                           tbody-class="participant-list-table-body">
                  </b-table>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn :disabled="!selectedParticipant"
                 @click="checkIn"
                 block>
            {{ $t('admin_check_in.title') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>

import {computed, onMounted, ref} from "@vue/composition-api";
import JwtScanner from "@/components/acc_booking/JwtScanner";
import _ from "lodash";
import {DateTime} from "luxon";

export default {
  name: 'HkiaraceCheckIn',
  components: {},
  setup(props, {root}) {
    const events = ref([]);
    const eventOptions = computed(() => {
      return events.value.map((e) => {
        return {text: root.$sanitizeHtml(e.name, {allowedTags: []}), value: e.id};
      })
    })
    const selectedEventId = ref(null);
    const getEvents = function () {
      root.$callAPI('hkiarace/admin/event/', {}, function (response) {
        events.value = response.body.events;
      })
    }

    const sessions = ref([]);
    const sessionOptions = computed(() => {
      return sessions.value.map((s) => {
        return {text: root.$sanitizeHtml(s.name, {allowedTags: []}), value: s.id};
      })
    })
    const selectedSessionId = ref(null);


    const onEventSelected = function (e) {
      selectedEventId.value = e;
      root.$callAPI('event/session/', {event_id: e}, function (response) {
        sessions.value = response.body.sessions;
        selectedSessionId.value = null;
      })
    };
    const onEventCleared = function () {
      selectedEventId.value = null;
      sessions.value = [];
      selectedSessionId.value = null;
    }

    const showDialog = ref(false);
    const searchInput = ref(null);
    const participantFields = ref([
      {
        key: 'event', label: root.$t('admin_participant.event'),
        formatter(value, key, item) {
          return root.$sanitizeHtml(item.event_session.event.name);
        },
      },
      {
        key: 'session', label: root.$t('admin_participant.session'),
        formatter(value, key, item) {
          return root.$sanitizeHtml(item.event_session.name);
        },
      },
      {
        key: 'account', label: root.$t('admin_participant.account'),
        formatter(value, key, item) {
          return item.account.surname + " " + item.account.given_name;
        },
      },
      {
        key: 'company', label: root.$t('admin_participant.company'),
        formatter(value, key, item) {
          return item.account.company.name;
        },
      },
      {
        key: 'checkin_time', label: root.$t('admin_participant.checkin_time'),
        formatter(value, key, item) {
          if (item.check_in_records.length > 0) {
            return DateTime.fromISO(item.check_in_records[0].checkin_time).toFormat('yyyy-MM-dd HH:mm');
          }

          return 'N/A';
        },
      },
    ]);
    const selectableTable = ref();
    const account = ref(null);
    const enrollment = ref(null);
    const participants = ref(null);
    const filteredParticipants = computed(() => {
      let filtered = participants.value;

      if (searchInput.value) {
        let filteringFields = [
          'account.name', 'account.surname_en', 'account.surname_zh', 'account.given_name_en', 'account.given_name_zh',
          'enrollment.event.name', 'enrollment.series.name', 'enrollment.session.name',
          'account.email', 'account.company.name',
        ];

        let results = [];

        let input = searchInput.value.toLowerCase();
        filtered.forEach(function (booking) {
          if (_.some(filteringFields, function (path) {
            let value = root.$resolve(booking, path);
            return value && value.toString().toLowerCase().indexOf(input) !== -1
          })) {
            results.push(booking);
          }
        });

        filtered = results;
      }

      return filtered;
    });
    const selectedParticipant = ref(null);
    const onParticipantSelected = function (selections) {
      if (selections.length > 0) {
        selectedParticipant.value = selections[0];
      } else {
        selectedParticipant.value = null;
      }
    }

    const search = async function () {
      let token = await root.$dialog.showAndWait(JwtScanner, {
        skipDecode: true,
        title: root.$t('admin_check_in.scan_qr_code'),
        targetKey: ['sub'],
        manualInputKey: ['hkiarace_no'],
        manualInputLabel: root.$t('admin_user.serial_no'),
      });

      if (token === undefined) return;

      let accountId = null;
      let hkiaraceNo = null;
      if (token instanceof Object) {
        accountId = token['sub']
        hkiaraceNo = token['hkiarace_no']
        token = null;
      }


      root.$callAPI('hkiarace/admin/info/', {
        event_id: selectedEventId.value,
        session_id: selectedSessionId.value,
        account_id: accountId,
        hkiarace_no: hkiaraceNo,
        token,
      }, (response) => {
        showDialog.value = true;
        account.value = response.body.account;
        enrollment.value = response.body.enrollment;
        participants.value = response.body.participant;

        if (response.body.participant.length === 1) {
          setTimeout(() => {
            selectableTable.value.selectRow(0)
          }, 500)
          // this.selectedParticipant = response.body.participant[0];
          // this.checkin();
        }
      }, function (response) {
        if (response.body.msg === 'invalid_token') {
          root.$showAlertMessage(root.$t('admin_check_in.invalid_token'));
        } else if (response.body.msg === 'token_expired') {
          root.$showAlertMessage(root.$t('admin_check_in.token_expired'));
        } else {
          root.$showAlertMessage();
        }
      })
    };
    const checkIn = function () {
      if (selectedParticipant.value && confirm(root.$t('admin_check_in.confirm_msg'))) {
        root.$callAPI('hkiarace/admin/event/participant/checkin/', {participant_id: selectedParticipant.value.id}, function (response) {
          root.$showAlertMessage('Checked In');
          showDialog.value = false;
        });
      }
    }

    onMounted(() => {
      getEvents()
    })

    return {
      events,
      eventOptions,
      selectedEventId,

      sessions,
      sessionOptions,
      selectedSessionId,

      onEventSelected,
      onEventCleared,

      showDialog,
      searchInput,
      participantFields,
      selectableTable,
      account,
      enrollment,
      participants,
      filteredParticipants,
      selectedParticipant,
      onParticipantSelected,

      search,
      checkIn,
    }
  },
}
</script>

<style lang="less">
.check-in {
  .check-in-no-record {
    font-size: 3rem;
    font-weight: bold;
  }

  .qr-code-reader-overlay {
    display: inline-table;
    height: 100%;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.5);

    label {
      display: table-cell;
      vertical-align: middle;
      text-align: center;
      color: white;
    }
  }
}
</style>
